/**
 * CR155: Hook for fetching schedule run data for combined review page
 * Aggregates canonical deals across all batch runs for a schedule_run_id
 */

import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import type { BatchRunScheduleRun } from '@/lib/scheduler/types';

export interface SourceRunSummary {
  source_type: string;
  batch_run_id: string;
  status: string;
  queue_status: string | null;
  deduped_count: number;
  created_at: string;
}

export interface CanonicalDealRow {
  id: string;
  batch_run_id: string;
  dedupe_key: string;
  canonical_deal_json: Record<string, unknown>;
  merged_sources_json: Array<{
    source_type: string;
    source_url: string | null;
    deal_link: string | null;
    staging_item_id: string;
    is_winner: boolean;
    completeness_score: number;
  }>;
  conflict_flags_json: Record<string, unknown> | null;
  computed_value_cad: number | null;
  effective_percent_back: number | null;
  review_status: string;
  review_reason: string | null;
  publish_status: string;
  created_at: string;
}

export interface ScheduleRunReviewData {
  scheduleRun: BatchRunScheduleRun | null;
  batchRuns: SourceRunSummary[];
  canonicalDeals: CanonicalDealRow[];
  counts: {
    enqueued: number;
    running: number;
    completed: number;
    failed: number;
    totalDeals: number;
  };
}

export function useScheduleRunReview(scheduleRunId: string | null) {
  const [data, setData] = useState<ScheduleRunReviewData>({
    scheduleRun: null,
    batchRuns: [],
    canonicalDeals: [],
    counts: { enqueued: 0, running: 0, completed: 0, failed: 0, totalDeals: 0 },
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();

  const fetchData = useCallback(async () => {
    if (!scheduleRunId) {
      setData({
        scheduleRun: null,
        batchRuns: [],
        canonicalDeals: [],
        counts: { enqueued: 0, running: 0, completed: 0, failed: 0, totalDeals: 0 },
      });
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // 1. Fetch schedule run metadata
      const { data: runData, error: runError } = await supabase
        .from('batch_run_schedule_runs')
        .select('*')
        .eq('id', scheduleRunId)
        .maybeSingle();

      if (runError) throw runError;
      if (!runData) {
        setError('Schedule run not found');
        return;
      }

      // 2. Fetch all batch runs for this schedule_run_id
      const { data: batchRuns, error: batchError } = await supabase
        .from('cr075_batch_runs')
        .select('id, status, schedule_queue_status, sources_enabled, deduped_count, created_at')
        .eq('schedule_run_id', scheduleRunId)
        .eq('is_deleted', false);

      if (batchError) throw batchError;

      // Compute source summaries
      const sourceSummaries: SourceRunSummary[] = (batchRuns || []).map((br) => {
        // Sources are stored as array like ["WEB"] or ["FLYER"]
        const sources = br.sources_enabled || [];
        const sourceType = sources[0] || 'UNKNOWN';
        return {
          source_type: sourceType,
          batch_run_id: br.id,
          status: br.status,
          queue_status: br.schedule_queue_status,
          deduped_count: br.deduped_count || 0,
          created_at: br.created_at,
        };
      });

      // Compute status counts from queue status
      const counts = {
        enqueued: sourceSummaries.filter(s => s.queue_status === 'ENQUEUED').length,
        running: sourceSummaries.filter(s => s.queue_status === 'RUNNING').length,
        completed: sourceSummaries.filter(s => s.queue_status === 'COMPLETED').length,
        failed: sourceSummaries.filter(s => s.queue_status === 'FAILED').length,
        totalDeals: 0,
      };

      // 3. Fetch canonical deals (deduped) from all batch runs
      const batchRunIds = (batchRuns || []).map(br => br.id);
      
      let canonicalDeals: CanonicalDealRow[] = [];
      if (batchRunIds.length > 0) {
        const { data: dealsData, error: dealsError } = await supabase
          .from('cr075_staging_deals_deduped')
          .select('*')
          .in('batch_run_id', batchRunIds)
          .order('created_at', { ascending: false });

        if (dealsError) throw dealsError;
        canonicalDeals = (dealsData || []) as unknown as CanonicalDealRow[];
        counts.totalDeals = canonicalDeals.length;
      }

      setData({
        scheduleRun: runData as BatchRunScheduleRun,
        batchRuns: sourceSummaries,
        canonicalDeals,
        counts,
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch schedule run data';
      setError(message);
      toast({
        title: 'Error',
        description: message,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }, [scheduleRunId, toast]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Poll while any runs are in progress
  useEffect(() => {
    const isInProgress = data.counts.enqueued > 0 || data.counts.running > 0;
    if (!isInProgress) return;

    const interval = setInterval(fetchData, 5000);
    return () => clearInterval(interval);
  }, [data.counts.enqueued, data.counts.running, fetchData]);

  return {
    ...data,
    loading,
    error,
    refetch: fetchData,
  };
}
